import { getDayOfWeek, getMonth, getOrdinalDay } from '../../util/dates'
import { capitalCase } from 'change-case'
import FrequencyParams = Models.FrequencyParams
import Day = Models.Day

import { FrequenciesList } from '../../util/contstants'
import { formatScheduleSummary } from '../../features/scheduled-segments/format'
import type { RecipeSchedule, ScheduleDefinition } from '../../features/scheduled-segments/types'

type ScheduleForFrequency = ScheduleDefinition | RecipeSchedule

const scheduleDefinition = (schedule: ScheduleForFrequency): ScheduleDefinition => {
    if ('id' in schedule) {
        return schedule.kind === 'cron'
            ? {
                  kind: 'cron',
                  timezone: schedule.timezone,
                  cron_expression: schedule.cron_expression ?? '',
              }
            : {
                  kind: 'once',
                  timezone: schedule.timezone,
                  run_once_at: schedule.run_once_at ?? '',
              }
    }

    return schedule
}

export const getFrequencyDescription = (
    frequency_params: FrequencyParams,
    schedule?: ScheduleForFrequency | null,
): string => {
    if (schedule) return formatScheduleSummary(scheduleDefinition(schedule))

    if (frequency_params?.frequency === FrequenciesList.Scheduled) return 'Scheduled'
    if (frequency_params?.frequency === FrequenciesList.Manually) return 'Manually'

    let frequencyDescription = ''
    switch (frequency_params?.frequency) {
        case FrequenciesList.AfterEveryDataSync:
            frequencyDescription += 'After every data sync'
            break
        case FrequenciesList.OncePerDay:
            frequencyDescription += 'Once per day'
            break
        default:
            console.error(
                'Cannot create frequency description for:',
                frequency_params?.frequency,
                'from',
                frequency_params,
            )
    }
    frequencyDescription += ', on '

    if (typeof frequency_params?.days !== 'undefined') {
        frequencyDescription += daysDescription(frequency_params?.days)
    } else {
        frequencyDescription += repeatsDescription(
            frequency_params?.repeat ?? '',
            frequency_params?.date ?? new Date(),
            false,
        )
        frequencyDescription = frequencyDescription.replace('on on', 'on')
    }
    return frequencyDescription
}

/**
 * Returns a properly comma'ed sentence for the given items.
 *
 * ie ["first","second","third"] -> "first, second, and third"
 *    ["first","second"] -> "first and second"
 * @param {string[]} items
 * @return {string}
 */
export const join = (items: string[]): string => {
    switch (items.length) {
        case 0:
            return ''
        case 1:
            return items[0]
        case 2:
            return `${items[0]} and ${items[1]}`
        default:
            return items.slice(0, items.length - 1).join(', ') + ', and ' + items[items.length - 1]
    }
}

const daysList = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const daysDescription = (days: Day[]): string => {
    // Capitalize each day ("monday" -> "Monday"), then sort in ascending order, with Monday being the first day
    const out = days
        .map((d) => capitalCase(d))
        .sort((a, b) => {
            if (a === b) {
                return 0
            }

            const aIndex = daysList.indexOf(a)
            const bIndex = daysList.indexOf(b)
            return aIndex < bIndex ? -1 : 1
        })

    return join(out)
}

export const repeatsDescription = (
    key: string,
    date: Date,
    uppercaseFirst: boolean = true,
): string => {
    const month = getMonth(date)
    const day = getDayOfWeek(date)
    const ordinalDay = getOrdinalDay(date)

    let description = ''
    switch (key) {
        case 'never':
            description = 'On ' + month + ' ' + ordinalDay + ' only'
            break
        case 'weekly':
            description = 'Every ' + day
            break
        case 'biweekly':
            description = 'Every two weeks on ' + day
            break
        case 'monthly':
            description = 'The ' + ordinalDay + ' of every month'
            break
        case 'quarterly':
            description = 'On the ' + ordinalDay + ' every 3 months'
            break
        case 'yearly':
            description = 'On ' + month + ' ' + ordinalDay + ' every year'
            break
        default:
            console.error('cannot render description for: ' + key)
            return ''
    }

    if (!uppercaseFirst) {
        description = description.charAt(0).toLowerCase() + description.slice(1)
    }

    return description
}

export function isObjectEmpty(variable: any) {
    if (typeof variable === 'object' && variable !== null) {
        return Object.keys(variable).length == 0
    }
    return false
}
